/**
 * GA4 page views for client-side navigation (see `trackPageView`).
 *
 * Every navigation is one page_view, with one exception: while the visitor
 * stays on /search, the search box rewrites ?query= (a `replace`, every 300 ms
 * of typing), so each prefix would count as a page view of its own. There the
 * reported path only moves once the query string has been unchanged for
 * SEARCH_SETTLE_MS — one page_view per settled search, the same rule the
 * `search` event follows. Arriving at /search (header search, a link, Back) and
 * leaving it are reported at once, as any navigation.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SEARCH_SETTLE_MS, trackPageView } from '@/lib/analytics';
import { LOCALE_ROUTES_LIVE, splitLocalePath } from '@/seo/urls';

/**
 * React Router matches case-insensitively and ignores a trailing slash. After
 * the URL move (web plan W2.1) the search page is `/{cc}/{lang}/search`.
 */
function isSearchPath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '').toLowerCase();
  if (path === '/search') return true;
  return LOCALE_ROUTES_LIVE && splitLocalePath(path)?.path === '/search';
}

function pathnameOf(path: string): string {
  const queryAt = path.indexOf('?');
  return queryAt === -1 ? path : path.slice(0, queryAt);
}

/** The path (pathname + search) to report as a page view for this location. */
export function usePageViewPath(
  pathname: string,
  search: string,
  settleMs: number = SEARCH_SETTLE_MS,
): string {
  const path = `${pathname}${search}`;
  const [reported, setReported] = useState(path);
  const staysOnSearch = isSearchPath(pathname) && isSearchPath(pathnameOf(reported));
  const current = staysOnSearch ? reported : path;

  // A navigation reported at once becomes the baseline for the next one.
  useEffect(() => {
    if (current !== reported) setReported(current);
  }, [current, reported]);

  // On /search: report the query string once it stops changing. Going back to
  // the one already reported (typing "x" then deleting it) cancels the wait.
  useEffect(() => {
    if (!staysOnSearch || path === reported) return undefined;
    const timer = window.setTimeout(() => setReported(path), settleMs);
    return () => window.clearTimeout(timer);
  }, [staysOnSearch, path, reported, settleMs]);

  return current;
}

/** Report a GA4 page_view per navigation (production only; see analytics). */
export function usePageViews(): void {
  const { pathname, search } = useLocation();
  const path = usePageViewPath(pathname, search);
  useEffect(() => trackPageView(path), [path]);
}
